import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { IdentityVerificationRemote } from "../../infrastructure/identity-verification.remote";
import {
  IdentityVerificationAction,
  IdentityVerificationMethod,
  IdentityVerificationStatus,
  IdVerseFailedAction,
  IdVerseStateData,
  StartNewIdVerseResponse,
  UpdateIdentityVerificationPayload,
  StoreVerificationData,
} from "../models/identity-verification.model";
import { IdentityVerificationStateService } from "./identity-verification.state.service";
import { map, mapTo, switchMap, take, tap } from "rxjs/operators";
import { SsoAccountFacade } from "../../../sso-account/domain/+state/sso-account.facade";
import { SsoAccountCheckoutState } from "../../../sso-account/domain/models/sso-account.model";
import { IdentityVerificationInfo } from "../models/identity-verification.model";

@Injectable({
  providedIn: "root",
})
export class IdentityVerificationFacade {
  constructor(
    private readonly remote: IdentityVerificationRemote,
    private readonly stateService: IdentityVerificationStateService,
    private readonly ssoAccountFacade: SsoAccountFacade,
  ) {}

  refresh(transactionId: string, customerId: number): Observable<void> {
    return this.remote.refresh(transactionId, customerId);
  }

  invalidate(transactionId: string, customerId: number): Observable<void> {
    return this.remote.invalidate(transactionId, customerId);
  }

  confirmSwitchToKba(): Observable<void> {
    return this.remote.confirmSwitchToKba();
  }

  mobileFlowCompleted(): Observable<boolean> {
    return this.remote.mobileFlowCompleted();
  }

  getIdVerseState$(hideLoader: boolean = false): Observable<IdVerseStateData> {
    if (this.stateService.isIdVerseStateLoaded()) {
      return this.stateService.getIdVerseState$();
    }
    return this.refreshIdVerseState(hideLoader).pipe(
      switchMap(() => this.stateService.getIdVerseState$()),
    );
  }

  refreshIdVerseState(hideLoader: boolean = false): Observable<IdVerseStateData> {
    return this.remote.getIdVerseState(hideLoader).pipe(
      tap((data: IdVerseStateData) => this.stateService.setIdVerseState(data)),
    );
  }

  isVerificationFailedWithNoRetry(): Observable<boolean> {
    return this.ssoAccountFacade.getSsoAccountCheckoutState$().pipe(
      take(1),
      switchMap((checkoutState) =>
        this.validateIsVerificationFailedWithNoRetry(checkoutState),
      ),
    );
  }

  isVerificationExpiredNoRetry(): Observable<boolean> {
    return this.ssoAccountFacade.getSsoAccountCheckoutState$().pipe(
      take(1),
      switchMap((checkoutState) =>
        this.validateIsVerificationExpiredWithNoRetry(checkoutState),
      ),
    );
  }

  private validateIsVerificationFailedWithNoRetry(
    checkoutState: SsoAccountCheckoutState,
  ): Observable<boolean> {
    if (
      checkoutState?.identityVerificationAction ===
        IdentityVerificationAction.ShowError &&
      checkoutState?.method === IdentityVerificationMethod.KBA
    ) {
      return of(true);
    }

    if (checkoutState?.method === IdentityVerificationMethod.IDVerse) {
      return this.getIdVerseState$().pipe(
        take(1),
        map((idVerseState: IdVerseStateData) => {
          if (
            idVerseState?.status === IdentityVerificationStatus.Failed &&
            idVerseState?.onFailedAction === IdVerseFailedAction.ShowError
          ) {
            return true;
          }
          return false;
        }),
      );
    }

    return of(false);
  }

  private validateIsVerificationExpiredWithNoRetry(
    checkoutState: SsoAccountCheckoutState,
  ): Observable<boolean> {
    if (
      checkoutState?.identityVerificationAction ===
        IdentityVerificationAction.ShowError &&
      checkoutState?.method === IdentityVerificationMethod.KBA
    ) {
      return of(false);
    }

    if (checkoutState?.method === IdentityVerificationMethod.IDVerse) {
      return this.getIdVerseState$().pipe(
        take(1),
        map((idVerseState: IdVerseStateData) => {
          if (
            idVerseState?.status === IdentityVerificationStatus.Failed &&
            idVerseState?.onFailedAction ===
              IdVerseFailedAction.ShowErrorOnExpired
          ) {
            return true;
          }
          return false;
        }),
      );
    }

    return of(false);
  }

  getCurrentInfo$(): Observable<IdentityVerificationInfo> {
    if (this.stateService.isCurrentIdentityVerificationInfoLoaded()) {
      return this.stateService.getCurrentInfo$();
    }

    return this.refreshCurrentInfo().pipe(
      switchMap(() => this.stateService.getCurrentInfo$()),
    );
  }

  refreshCurrentInfo(): Observable<void> {
    return this.remote.getInfo().pipe(
      tap((data: IdentityVerificationInfo) =>
        this.stateService.setCurrentInfo(data),
      ),
      mapTo(undefined),
    );
  }

  updateIdentityVerification(
    payload: UpdateIdentityVerificationPayload,
  ): Observable<void> {
    return this.remote.updateIdentityVerification(payload);
  }

  startNewIdVerse(customerId: number): Observable<StartNewIdVerseResponse> {
    return this.remote.startNewIdVerse(customerId);
  }

  startNewKba(customerId: number): Observable<void> {
    return this.remote.startNewKba(customerId);
  }

  setVerified(customerId: number): Observable<void> {
    return this.remote.setVerified(customerId);
  }

  storeVerification(customerId: number): Observable<StoreVerificationData> {
    return this.remote.storeVerification(customerId);
  }

  clearState(): void {
    this.stateService.clearState();
  }
}
